import React, { createContext, useContext, useMemo, useState } from "react";
import { useBlocker } from "react-router-dom";
import { defaultGlobalConfirmationModalValues } from "../components/global/GlobalConfirmationModal";

interface AppRouteContextProps {
  children: React.ReactNode;
}

export interface IGlobalRouteRestrictionDetails {
  isRoutingBlocked: boolean;
  isConfirmationModalOpen: boolean;
  secondaryButtonLabel: string;
  primaryButtonLabel: string;
  secondaryButtonOnClick: () => void;
  primaryButtonOnClick: () => void;
  headerText: string;
  bodyText: string;
  navigatePath: string;
}

export interface IAppRouteContextType {
  routeRestrictionDetails: IGlobalRouteRestrictionDetails;
  setRouteRestrictionDetails: React.Dispatch<
    React.SetStateAction<IGlobalRouteRestrictionDetails>
  >;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AppRouteContext = createContext<IAppRouteContextType | undefined>(
  undefined,
);

const useAppRouteContext = () => {
  const context = useContext(AppRouteContext);
  if (!context) {
    throw new Error(
      "useAppRouteContext must be used within a AppRouteContextProvider",
    );
  }
  return context;
};

const defaultRouteRestrictionValues = {
  ...defaultGlobalConfirmationModalValues,
  isRoutingBlocked: false,
  isConfirmationModalOpen: false,
  navigatePath: "",
};

const AppRouteContextProvider = ({ children }: AppRouteContextProps) => {
  const [routeRestrictionDetails, setRouteRestrictionDetails] =
    useState<IGlobalRouteRestrictionDetails>(defaultRouteRestrictionValues);

  useBlocker(({ currentLocation, nextLocation }) => {
    if (
      routeRestrictionDetails.isRoutingBlocked &&
      currentLocation.pathname !== nextLocation.pathname
    ) {
      setRouteRestrictionDetails({
        ...routeRestrictionDetails,
        // Including search to ensure that queryParams are passed when confirmation modals handle navigation.
        navigatePath: nextLocation.pathname + nextLocation.search,
        isConfirmationModalOpen: true,
      });
      return true;
    }
    return false;
  });

  const contextValue = useMemo<IAppRouteContextType>(
    () => ({
      routeRestrictionDetails,
      setRouteRestrictionDetails,
    }),
    [routeRestrictionDetails],
  );

  return (
    <AppRouteContext.Provider value={contextValue}>
      {children}
    </AppRouteContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export { AppRouteContextProvider, useAppRouteContext };
